import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetShadowOrderPlans } from "@/services/order/queries";
import { getTimeSinceText } from "@/utils/timestamp";
import {
	Chip,
	DataTable,
	SmartSelect,
	useServerTableState,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { groupShadowSteps, savingsPercentage } from "./shadow";

type PlanRow = ReturnType<typeof groupShadowSteps>[number];

export default function OrderDetailsShadowPlan({
	orderId,
}: { orderId: string }) {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const { data, isLoading, isFetching, error, refetch } =
		useGetShadowOrderPlans(orderId);
	const [selectedId, setSelectedId] = useState("");
	const server = useServerTableState();
	const jobs = data?.results ?? [];
	const job = jobs.find((job) => job.id === selectedId) ?? jobs[0];
	const v1 = job?.result?.plans.find((plan) => plan.name === "v1");
	const v2 = job?.result?.plans.find((plan) => plan.name === "v2");
	const saving = job?.result?.savings_vs_v1;
	const reduction = savingsPercentage(v1, saving);
	const rows = useMemo(() => groupShadowSteps(v2?.steps ?? []), [v2]);
	const number = new Intl.NumberFormat(lang, { maximumFractionDigits: 0 });
	const percent = new Intl.NumberFormat(lang, { maximumFractionDigits: 1 });

	const columns = useMemo<ColumnDef<PlanRow>[]>(() => {
		const number = new Intl.NumberFormat(lang, { maximumFractionDigits: 0 });
		const percent = new Intl.NumberFormat(lang, { maximumFractionDigits: 1 });
		const sum = (
			row: PlanRow,
			pick: (step: PlanRow["steps"][number]) => number,
		) => row.steps.reduce((total, step) => total + pick(step), 0);
		const columns: ColumnDef<PlanRow>[] = [
			{
				id: "step",
				header: t("orderPlan.columns.step"),
				cell: ({ row }) =>
					row.original.start === row.original.end
						? number.format(row.original.start)
						: `${number.format(row.original.start)}–${number.format(
								row.original.end,
							)}`,
			},
			{
				id: "method",
				header: t("orderPlan.columns.method"),
				cell: ({ row }) => {
					const { steps } = row.original;
					return (
						<span className="font-medium">
							{steps[0].suggestion.method === "QUICK_SELL"
								? t("orderPlan.quickSells", { count: steps.length })
								: t("orderPlan.snipe")}
						</span>
					);
				},
			},
			{
				id: "player",
				header: t("orderPlan.columns.player"),
				cell: ({ row }) => {
					const { suggestion } = row.original.steps[0];
					return suggestion.method === "QUICK_SELL" ? (
						"—"
					) : (
						<span>
							{suggestion.player.nick_name}{" "}
							<span className="text-secondary-text-100">
								· {suggestion.player.rating}
							</span>
						</span>
					);
				},
			},
			{
				id: "sender",
				header: t("orderPlan.columns.sender"),
				cell: ({ row }) => {
					const { steps } = row.original;
					if (steps.length > 1) return "—";
					const sender = steps[0].suggestion.sender_stats.data;
					return (
						<div>
							<div dir="ltr">{sender.ea_email}</div>
							<div className="mt-1 text-xs text-secondary-text-100">
								{t("orderPlan.priority")}:{" "}
								{t(`orderPlan.priorities.${sender.resolved_priority.value}`)} ·{" "}
								{t("orderPlanV2.available", {
									amount: number.format(sender.available_coins),
								})}
							</div>
						</div>
					);
				},
			},
			{
				id: "successRate",
				header: t("orderPlan.columns.successRate"),
				cell: ({ row }) => {
					const { steps } = row.original;
					return steps.length > 1
						? "—"
						: `${percent.format(steps[0].probability * 100)}%`;
				},
			},
			{
				id: "planned",
				header: t("orderPlan.columns.planned"),
				meta: { className: "text-end tabular-nums" },
				cell: ({ row }) =>
					number.format(
						sum(row.original, (step) => step.suggestion.to_transfer),
					),
			},
			{
				id: "cost",
				header: t("orderPlanV2.expectedCost"),
				meta: { className: "text-end tabular-nums" },
				cell: ({ row }) =>
					number.format(
						sum(row.original, (step) => step.expected_completion_cost),
					),
			},
			{
				id: "tax",
				header: t("orderPlanV2.estimatedTax"),
				meta: { className: "text-end tabular-nums" },
				cell: ({ row }) => {
					const cost = sum(
						row.original,
						(step) => step.expected_completion_cost,
					);
					const tax = sum(row.original, (step) => step.expected_tax);
					return `${percent.format(cost ? (tax / cost) * 100 : 0)}%`;
				},
			},
		];
		return columns.map((column) => ({ ...column, enableSorting: false }));
	}, [t, lang]);

	const pageIndex = Math.min(
		server.pageIndex,
		Math.max(0, Math.ceil(rows.length / server.pageSize) - 1),
	);
	const empty =
		job?.status === "PENDING"
			? (["pendingTitle", "pendingDescription"] as const)
			: job?.status === "FAILED"
				? (["failedTitle", "failedDescription"] as const)
				: job
					? (["noTransfers", "noTransfersDescription"] as const)
					: (["emptyTitle", "emptyDescription"] as const);

	return (
		<div className="space-y-4">
			{job && (
				<div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
					<Chip variant="info">
						{t("orderPlanV2.executed", {
							version: t(
								`executionPlanVersion.short.${
									job.shadow_version === "v1" ? "v2" : "v1"
								}`,
							),
						})}
					</Chip>
					{job.result && v1 && v2 && (
						<>
							<span>
								{t("orderPlanV2.v1Cost", {
									amount: number.format(v1.expected_completion_cost),
								})}
							</span>
							<span>
								{t("orderPlanV2.v2Cost", {
									amount: number.format(v2.expected_completion_cost),
								})}
							</span>
							{saving != null && reduction != null ? (
								<Chip variant={saving >= 0 ? "success" : "warning"}>
									{t("orderPlanV2.saving", {
										amount: number.format(saving),
										percent: percent.format(reduction),
									})}
								</Chip>
							) : (
								<span className="text-secondary-text-100">
									{t("orderPlanV2.notComparable")}
								</span>
							)}
						</>
					)}
				</div>
			)}
			{jobs.length > 1 && job && (
				<SmartSelect
					aria-label={t("orderPlanV2.snapshot")}
					value={job.id}
					onChange={setSelectedId}
					items={jobs.map((job) => ({
						value: job.id,
						label: `${getTimeSinceText(
							job.captured_at,
							t,
							lang,
						)} · ${number.format(job.remaining_at_selection)} · ${t(
							`orderPlanV2.status.${
								job.status as "PENDING" | "COMPLETED" | "FAILED"
							}`,
						)}`,
					}))}
				/>
			)}
			<DataTable
				columns={columns}
				data={rows.slice(
					pageIndex * server.pageSize,
					(pageIndex + 1) * server.pageSize,
				)}
				totalItems={rows.length}
				server={{ ...server, pageIndex }}
				pageSizeOptions={[10, 20, 40, 100]}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => String(row.start)}
				emptyStateProps={{
					title: t(`orderPlanV2.${empty[0]}`),
					description: t(`orderPlanV2.${empty[1]}`),
					mode: "data",
				}}
			/>
		</div>
	);
}
